import { useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';

import { RunStatus } from '../../../../../../backend/src/types/types';
import { Icon } from '../../../components/icon';
import { SearchInput } from '../../../components/search-input';
import { TableFooter } from '../../../components/table-footer';
import { Case } from '@/api/types/backend-dto-types';
import { isProcessingStatus } from '@/constants/pipelineStatus';

const fallbackTableData: Case[] = [];
const columnHelper = createColumnHelper<Case>();

// add columns for anything we might want to filter by
const columns = [columnHelper.accessor('name', {}), columnHelper.accessor('createdDate', {})];

export function MatterList({ matters }: { matters?: Case[] }) {
  // const numberFormatter = new Intl.NumberFormat();
  const [globalFilter, setGlobalFilter] = useState('');

  // using tanstack table so we have things like sorting, pagination, fitlering etc. easily
  const table = useReactTable({
    data: matters ?? fallbackTableData,
    columns,
    initialState: {
      sorting: [
        {
          id: 'createdDate',
          desc: false,
        },
      ],
      pagination: {
        pageSize: 30,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter,
    },
  });

  return (
    <div>
      <SearchInput
        placeholder="Search matters..."
        value={globalFilter}
        onChange={(e) => setGlobalFilter(e.target.value)}
        className="my-6 max-w-sm"
      />

      <div className="my-6 grid grid-cols-[repeat(auto-fit,minmax(24rem,1fr))] gap-4">
        {matters &&
          table.getRowModel().rows.map((row) => {
            const isCaseProcessing =
              row.original.runs.at(0) && isProcessingStatus(row.original.runs[0].status as RunStatus);

            return (
              <Link
                to={`/matters/${row.original.id}`}
                key={row.original.id}
                className="group animate-in fade-in flex w-full flex-col rounded-md border border-gray-200 shadow-lg/2 duration-150 hover:shadow-lg/10 hover:shadow-teal-500"
              >
                <h2 className="flex items-center gap-3 rounded-t-md bg-gray-50 p-3 px-4.5 text-base font-medium text-gray-700 duration-150 group-hover:text-gray-900">
                  <Icon className="lucide--book text-gray-500 duration-150 group-hover:text-teal-500" />
                  {row.original.name}

                  {/* for when code is added to the Case entity */}
                  {/* {row.original.code && (
                <span className="rounded-md border border-gray-300 bg-white p-0.5 px-1 pb-0.25 text-xs text-gray-500">
                  {row.original.code}
                </span>
              )} */}
                </h2>

                <div className="flex grow flex-col justify-between gap-3 rounded-b-md border-t border-gray-200 p-4 px-5 text-gray-700">
                  <p className="line-clamp-2">{row.original.legalIssues}</p>

                  <div className="flex items-end justify-between">
                    <div>
                      <p className="mb-0.5 flex items-center gap-2">
                        <Icon className="lucide--group text-gray-500" />
                        <span className="font-medium">
                          {0} <span className="font-normal">subsets</span>
                        </span>
                      </p>
                      <p className="flex items-center gap-2">
                        <Icon className="lucide--file-text text-gray-500" />
                        <span className="font-medium">
                          {/* for when docsCount is added to the Case entity */}
                          {/* {numberFormatter.format(matter.docsCount)} */}
                          <span>0 </span>
                          <span className="font-normal">docs</span>
                        </span>
                      </p>
                    </div>

                    <div
                      className={`${isCaseProcessing ? 'border-sky-300 bg-sky-50 text-sky-600' : 'border-gray-300 bg-gray-50'} flex items-center gap-1.25 rounded-md border px-1.25 py-0.75 text-xs`}
                    >
                      <Icon
                        className={`${isCaseProcessing ? 'lucide--loader-circle animate-spin text-sky-500' : 'lucide--circle-check text-teal-500'} duration-1500`}
                      />
                      <span>{isCaseProcessing ? 'Processing' : 'Processed'}</span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
      </div>
      <TableFooter table={table} plural="matters" />
    </div>
  );
}
